<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WasteBinReadingModal',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    objectId: {
      type: Number,
      required: true,
    },
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    noPrimaryButton: true,
    closeButtonText: $fn.value.gettext('Close'),
    size: 'sm',
  }));
  const b_5534025912102772_auto = computed(() => false);
  const b_5430362791568289_variantMap = computed(() => ({
    empty: 'success',
    full: 'warning',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_5534025912102772_auto: b_5534025912102772_auto.value,
    b_5430362791568289_variantMap: b_5430362791568289_variantMap.value,
  }));

  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = async () => {
    return $fn.value
      .fetch('waste_bins/get_reading', { parameters: { id: props.objectId } })
      .then((d) => new $fn.value.Resource(d));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_5534025912102772_auto', nodeId: '5534025912102772' },
      { name: 'b_5430362791568289_variantMap', nodeId: '5430362791568289' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_5534025912102772_auto,
      b_5430362791568289_variantMap,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return { title: $fn.value.gettext('Waste bin reading') };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.objectId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('objectId');
    }
  );
  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_amd3y6i4q_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2286348834130966_setData_hz9w73o1d_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event && $event.data.response.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2286348834130966_request_ter3pocvi_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'waste_bins/update_reading',
      data: {
        id: item.value.id,
        state: item.value.state == 'full' ? 'empty' : 'full',
      },
    };
  };

  const act_7315092382398562_closeModal_amd3y6i4q =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_amd3y6i4q_getActionArgs,
    });
  const inst_evh_7315092382398562_close_3qpcrm2vo =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_amd3y6i4q,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_3qpcrm2vo],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_zt5hutj23 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_wed11ps9s =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_zt5hutj23,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_wed11ps9s],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_79ej5f5g9 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_y2itmjysg =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_79ej5f5g9,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_y2itmjysg],
    event: 'close',
  });
  const act_2286348834130966_setData_hz9w73o1d = new actionTypes.SetDataAction({
    actionArgs: act_2286348834130966_setData_hz9w73o1d_getActionArgs,
  });
  const inst_evh_2286348834130966_success_wkx4r5tf4 =
    new actionTypes.EventHandlerImpl({
      action: act_2286348834130966_setData_hz9w73o1d,
      event: 'success',
      displayName: 'setData',
    });
  const act_2286348834130966_request_ter3pocvi = new actionTypes.RequestAction({
    actionArgs: act_2286348834130966_request_ter3pocvi_getActionArgs,
    displayName: 'toggleState',
    events: [inst_evh_2286348834130966_success_wkx4r5tf4],
  });
  const inst_evh_2286348834130966_clickPreventStop_elb9ztop5 =
    new actionTypes.EventHandlerImpl({
      action: act_2286348834130966_request_ter3pocvi,
      event: 'click.prevent.stop',
      displayName: 'toggleState',
    });
  const inst_evh_2286348834130966_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2286348834130966_clickPreventStop_elb9ztop5],
      event: 'click.prevent.stop',
    });
  const act_2140198529698299_closeComponent_34wpnc1gk =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2140198529698299_clickPreventStop_dqnng6dq6 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_34wpnc1gk,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2140198529698299_clickPreventStop_dqnng6dq6],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_3qpcrm2vo,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_wed11ps9s,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_y2itmjysg,
    inst_evh_8564662037462133_close,
    inst_evh_2286348834130966_success_wkx4r5tf4,
    inst_evh_2286348834130966_clickPreventStop_elb9ztop5,
    toggleState: inst_evh_2286348834130966_clickPreventStop_elb9ztop5,
    inst_evh_2286348834130966_clickPreventStop,
    inst_evh_2140198529698299_clickPreventStop_dqnng6dq6,
    inst_evh_2140198529698299_clickPreventStop,
  };

  const evh_7315092382398562_close_3qpcrm2vo = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_3qpcrm2vo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_wed11ps9s = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_wed11ps9s.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_y2itmjysg = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_y2itmjysg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2286348834130966_success_wkx4r5tf4 = async (event, scope) => {
    return await actions.inst_evh_2286348834130966_success_wkx4r5tf4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2286348834130966_clickPreventStop_elb9ztop5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2286348834130966_clickPreventStop_elb9ztop5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2286348834130966_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2286348834130966_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop_dqnng6dq6 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop_dqnng6dq6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WasteBinReadingModal"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm name="waste_bin_reading" :id="formId" class="row" ref="mainForm">
        <ItpCol :auto="b_5534025912102772_auto">
          <ItpFormGroup
            row
            :label-cols-md="3"
            :content-cols-md="9"
            name="waste_bin_number"
            :label="pgettext('WasteBinReadingModal', 'Bin Number')"
          >
            <ItpFormInput
              name="waste_bin_number"
              v-model="item.waste_bin_number"
              :value="item.waste_bin_number"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (item.waste_bin_number =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
              readonly
            >
            </ItpFormInput>
          </ItpFormGroup>
          <ItpFormGroup
            row
            :label-cols-md="3"
            :content-cols-md="9"
            name="timestamp"
            :label="pgettext('WasteBinReadingModal', 'Time')"
          >
            <ItpFormPlaintext id="timestamp">
              {{ datetime(item.timestamp) }}
            </ItpFormPlaintext>
          </ItpFormGroup>
          <ItpFormGroup
            row
            :label-cols-md="3"
            :content-cols-md="9"
            name="identity"
            :label="pgettext('WasteBinReadingModal', 'Device')"
          >
            <ItpFormPlaintext id="identity">
              {{ item.identity }}
            </ItpFormPlaintext>
          </ItpFormGroup>
          <ItpFormGroup
            row
            :label-cols-md="3"
            :content-cols-md="9"
            name="state"
            :label="pgettext('WasteBinReadingModal', 'State')"
          >
            <ItpBox alignItems="center">
              <ItpBadge
                :value="item.state"
                :variantMap="b_5430362791568289_variantMap"
                no-box-shadow
                class="me-3"
              >
                {{ pgettext('WasteBinReadingStates', item.state, 'StartCase') }}
              </ItpBadge>
              <ItpButton
                :text="pgettext('WasteBinReadingModal', 'Toggle state')"
                variant="light"
                size="sm"
                @click.prevent.stop="
                  evh_2286348834130966_clickPreventStop($event, {})
                "
              >
              </ItpButton>
            </ItpBox>
          </ItpFormGroup>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2140198529698299_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
